/* glass-logo.js — 3D glass logo controller (block + header logo).
   Lazily loads the Three.js bundle (assets/three-glass.min.js, ~175 KB gz) only
   when needed, extrudes the merchant's SVG into a physical-glass body and renders
   it. A flat <img> fallback shows for reduce-motion, no WebGL, a failed fetch, or
   before the 3D loads.

   data-motion: always | hover | intro
     always  continuous auto-rotate (pauses off-screen)
     hover   static frozen glass; animates only while hovered (GPU idle otherwise)
     intro   drives in once, then freezes
   data-defer="idle": load the bundle on requestIdleCallback (header above-fold). */
(function () {
  if (window.__neGlassLogo) { window.__neGlassLogo.rescan(); return; }

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var bundlePromise = null;

  function loadBundle(url) {
    if (window.NEThree) return Promise.resolve(window.NEThree);
    if (bundlePromise) return bundlePromise;
    bundlePromise = new Promise(function (res, rej) {
      var s = document.createElement('script');
      s.src = url; s.async = true;
      s.onload = function () { window.NEThree ? res(window.NEThree) : rej(new Error('NEThree missing')); };
      s.onerror = function () { rej(new Error('bundle failed')); };
      document.head.appendChild(s);
    });
    return bundlePromise;
  }

  function hasWebGL() {
    try {
      var c = document.createElement('canvas');
      return !!(window.WebGLRenderingContext && (c.getContext('webgl2') || c.getContext('webgl')));
    } catch (e) { return false; }
  }

  function num(el, a, d) { var v = parseFloat(el.getAttribute(a)); return isFinite(v) ? v : d; }

  /* Material presets — coherent glass "textures". Values are already in the units
     MeshPhysicalMaterial expects (roughness/transmission/iridescence/clearcoat 0-1,
     ior ~1.0-2.4, thickness/dispersion/env as-is). Picked via data-material-preset;
     'custom' (or unknown) falls back to the individual data-* attrs. */
  var MATERIAL_PRESETS = {
    crystal:  { tint: '#eaf2ff', transmission: 1.00, roughness: 0.02, ior: 1.50, thickness: 3.0, iridescence: 0.00, dispersion: 0.4, clearcoat: 0.40, env: 1.4, metalness: 0 },
    prisma:   { tint: '#bcd2ff', transmission: 1.00, roughness: 0.06, ior: 1.55, thickness: 2.2, iridescence: 0.92, dispersion: 1.6, clearcoat: 0.30, env: 1.3, metalness: 0 },
    frosted:  { tint: '#dfe6f0', transmission: 0.60, roughness: 0.45, ior: 1.40, thickness: 1.8, iridescence: 0.15, dispersion: 0.2, clearcoat: 0.15, env: 1.0, metalness: 0 },
    tinted:   { tint: '#6f8cff', transmission: 0.88, roughness: 0.08, ior: 1.48, thickness: 2.6, iridescence: 0.10, dispersion: 0.5, clearcoat: 0.35, env: 1.2, metalness: 0 },
    bubble:   { tint: '#cfe0ff', transmission: 1.00, roughness: 0.04, ior: 1.40, thickness: 1.2, iridescence: 1.00, dispersion: 2.8, clearcoat: 0.25, env: 1.7, metalness: 0 },
    metal:    { tint: '#c2ccdc', transmission: 0.10, roughness: 0.08, ior: 1.70, thickness: 4.0, iridescence: 0.30, dispersion: 0.3, clearcoat: 1.00, env: 2.0, metalness: 0.6 },
    amber:    { tint: '#ffb765', transmission: 0.92, roughness: 0.10, ior: 1.50, thickness: 3.0, iridescence: 0.20, dispersion: 0.6, clearcoat: 0.30, env: 1.2, metalness: 0 },
    obsidian: { tint: '#2a3244', transmission: 0.35, roughness: 0.08, ior: 1.52, thickness: 4.4, iridescence: 0.12, dispersion: 0.4, clearcoat: 0.50, env: 1.4, metalness: 0 },
    neon:     { tint: '#25f7ff', transmission: 0.96, roughness: 0.05, ior: 1.48, thickness: 1.6, iridescence: 0.98, dispersion: 2.4, clearcoat: 0.30, env: 1.85, metalness: 0 }
  };

  /* Presets whose identity IS a colour — the merchant can't override the tint
     (a red obsidian / blue amber would be nonsense). Every other preset exposes
     a "use custom colour" toggle (data-tint-override). */
  var IMPLIED_TINT = { amber: 1, obsidian: 1 };

  /* Environment looks — reflection/light mood via the 3 lights (colors + intensity).
     Purely procedural, no HDRI assets. Picked via data-env-look. */
  var ENV_LOOKS = {
    studio:   { key: 0xffffff, keyI: 2.2, rim: 0x88aaff, rimI: 1.4, amb: 0xffffff, ambI: 0.25 },
    soft:     { key: 0xfff4e8, keyI: 1.6, rim: 0xcdd8ff, rimI: 1.1, amb: 0xffffff, ambI: 0.40 },
    contrast: { key: 0xffffff, keyI: 3.2, rim: 0x4466cc, rimI: 1.8, amb: 0xffffff, ambI: 0.12 },
    neon:     { key: 0xff10d6, keyI: 2.7, rim: 0x00f2ff, rimI: 2.3, amb: 0x180a30, ambI: 0.28 },
    sunset:   { key: 0xff9a4d, keyI: 2.4, rim: 0xff5a7a, rimI: 1.4, amb: 0x331a00, ambI: 0.30 },
    mono:     { key: 0xffffff, keyI: 2.6, rim: 0xffffff, rimI: 1.2, amb: 0xffffff, ambI: 0.30 }
  };

  // Reflection/refraction environment. RoomEnvironment alone is a neutral studio —
  // glass has nothing chromatic to reflect (iridescence) or split (dispersion), so the
  // rainbow never reads. When `chroma` > 0 we add saturated coloured panels as area
  // lights, scaled by the iridescence/dispersion sliders. This matters because
  // transmission can only refract the GL scene, NOT the DOM background behind the canvas.
  function buildEnvTexture(NE, THREE, r, chroma) {
    var pmrem = new THREE.PMREMGenerator(r);
    var envScene = new NE.RoomEnvironment();
    if (chroma > 0.01) {
      var panels = [
        [0xff2ad0, -7, 4, -3], [0x1ad8ff, 7, 3, -3], [0xffcf3a, 0, -6, 6],
        [0x7a3cff, -5, -3, 6], [0x39ff88, 5, 5, 5]
      ];
      var boost = 0.6 + chroma * 2.4; // HDR: >1 makes the panels bright emitters
      for (var i = 0; i < panels.length; i++) {
        var p = panels[i];
        var col = new THREE.Color(p[0]).multiplyScalar(boost);
        var m = new THREE.Mesh(new THREE.PlaneGeometry(8, 8), new THREE.MeshBasicMaterial({ color: col }));
        m.position.set(p[1], p[2], p[3]); m.lookAt(0, 0, 0);
        envScene.add(m);
      }
    }
    var tex = pmrem.fromScene(envScene, 0.02).texture;
    pmrem.dispose();
    return tex;
  }

  function Glass(el, NE) {
    var THREE = NE.THREE;
    this.el = el; this.THREE = THREE; this.NE = NE;
    this.canvas = el.querySelector('.ne-glogo__canvas');
    this.fallback = el.querySelector('.ne-glogo__fallback');
    this.running = false; this.raf = 0; this.last = 0; this.mesh = null; this.stopAt = 0;
    this.motion = el.getAttribute('data-motion') || 'always';
    this.tx = 0; this.ty = 0; this.mx = 0; this.my = 0; this.pointer = false; this.inView = true;

    // preserveDrawingBuffer keeps the last frame visible when the loop stops
    // (frozen glass for hover/intro) without re-rendering.
    var r = this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, antialias: true, alpha: true, powerPreference: 'low-power', preserveDrawingBuffer: true });
    r.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.75));
    r.toneMapping = THREE.ACESFilmicToneMapping;
    r.toneMappingExposure = 1.1;
    r.outputColorSpace = THREE.SRGBColorSpace;

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(38, 1, 0.1, 100);
    this.camera.position.set(0, 0, 7);

    // Resolve preset + the two rainbow values up front so the environment can be
    // tinted to match how much rainbow the merchant dialled in.
    var preset = el.getAttribute('data-material-preset');
    var P = MATERIAL_PRESETS[preset];
    function mval(attr, k, d) { return (P && k in P) ? P[k] : num(el, attr, d); }
    var iridVal = mval('data-iridescence', 'iridescence', 0.7);
    var dispVal = mval('data-dispersion', 'dispersion', 0.6);
    // chroma tracks the sliders: neutral glass stays neutral, iridescent/dispersive
    // presets get a colourful environment that actually splinters into a rainbow.
    var chroma = Math.min(1, Math.max(iridVal, dispVal / 3));
    this.scene.environment = buildEnvTexture(NE, THREE, r, chroma);

    var EL = ENV_LOOKS[el.getAttribute('data-env-look')] || ENV_LOOKS.studio;
    var key = new THREE.DirectionalLight(EL.key, EL.keyI); key.position.set(4, 6, 5); this.scene.add(key);
    var rim = new THREE.DirectionalLight(EL.rim, EL.rimI); rim.position.set(-6, -2, -4); this.scene.add(rim);
    this.scene.add(new THREE.AmbientLight(EL.amb, EL.ambI));

    // Tint: a colour-identity preset (amber/obsidian) always keeps its own tint;
    // any other preset lets the merchant override it via the custom-colour toggle.
    var tint;
    if (P) {
      tint = (!IMPLIED_TINT[preset] && el.getAttribute('data-tint-override') === '1')
        ? (el.getAttribute('data-tint') || P.tint) : P.tint;
    } else {
      tint = el.getAttribute('data-tint') || '#bcd2ff';
    }
    this.mat = new THREE.MeshPhysicalMaterial({
      color: new THREE.Color(tint), metalness: (P && 'metalness' in P) ? P.metalness : 0,
      roughness: mval('data-roughness', 'roughness', 0.06),
      transmission: mval('data-transmission', 'transmission', 1),
      ior: mval('data-ior', 'ior', 1.45),
      thickness: mval('data-thickness', 'thickness', 2.2),
      // Iridescence at the physical max film IOR + a wide thickness band → visible
      // oil-slick fringes on the bevels instead of a faint uniform tint.
      iridescence: iridVal, iridescenceIOR: 2.33,
      iridescenceThicknessRange: [140, 140 + iridVal * 1200],
      clearcoat: mval('data-clearcoat', 'clearcoat', 0.3), clearcoatRoughness: 0.15,
      envMapIntensity: mval('data-env', 'env', 1.2),
      side: THREE.DoubleSide, transparent: false,
      // High attenuationDistance so 100% transmission reads as genuinely clear glass.
      attenuationColor: new THREE.Color(tint), attenuationDistance: 18
    });
    // Dispersion punched: three's realistic range is ~[0,1]; the schema allows up to 8
    // and the chroma environment gives saturated light to split → spectral fringing shows.
    if ('dispersion' in this.mat) this.mat.dispersion = dispVal;

    this.spin = num(el, 'data-spin', 0.5);
    this.mouse = el.getAttribute('data-mouse') === '1';
    this.fill = num(el, 'data-fill', 82) / 100;
    this.ox = num(el, 'data-ox', 0) / 100;
    this.oy = num(el, 'data-oy', 0) / 100;
    if (el.getAttribute('data-shadow') === '1') this._initShadow(num(el, 'data-shadow-strength', 40) / 100);

    var self = this;
    this._onResize = function () { self._resize(); self.renderOnce(); };
    window.addEventListener('resize', this._onResize, { passive: true });
    if (this.mouse || this.motion === 'hover') {
      this._onEnter = function () { if (self.motion === 'hover') self.start(); };
      this._onLeaveEl = function () { if (self.motion === 'hover') self.stopAt = performance.now() + 450; self.pointer = false; };
      el.addEventListener('pointerenter', this._onEnter);
      el.addEventListener('pointerleave', this._onLeaveEl);
    }
    if (this.mouse) {
      this._onMove = function (e) {
        var b = el.getBoundingClientRect();
        self.mx = ((e.clientX - b.left) / b.width - 0.5) * 2;
        self.my = ((e.clientY - b.top) / b.height - 0.5) * 2;
        self.pointer = true;
        if (!self.running) self.renderOnce();
      };
      el.addEventListener('pointermove', this._onMove, { passive: true });
    }
    this._onLost = function (ev) { ev.preventDefault(); self.stop(); self._showFallback(); };
    this.canvas.addEventListener('webglcontextlost', this._onLost);

    this._buildFromSVG(el.getAttribute('data-svg'));
  }

  Glass.prototype._buildFromSVG = function (url) {
    if (!url) { this._showFallback(); return; }
    var self = this, THREE = this.THREE, el = this.el;
    fetch(url).then(function (r) { return r.text(); }).then(function (txt) {
      var data = new self.NE.SVGLoader().parse(txt);
      var shapes = [];
      data.paths.forEach(function (p) { self.NE.SVGLoader.createShapes(p).forEach(function (s) { shapes.push(s); }); });
      if (!shapes.length) { self._showFallback(); return; }
      var geo = new THREE.ExtrudeGeometry(shapes, {
        depth: num(el, 'data-depth', 26), bevelEnabled: true,
        bevelThickness: num(el, 'data-bevel-depth', 5),
        bevelSize: num(el, 'data-bevel-size', 6),
        bevelSegments: 4, curveSegments: num(el, 'data-curve', 20)
      });
      geo.scale(1, -1, 1); geo.computeVertexNormals(); geo.center();
      geo.computeBoundingBox();
      self._size = new THREE.Vector3(); geo.boundingBox.getSize(self._size);
      self.mesh = new THREE.Mesh(geo, self.mat); self.scene.add(self.mesh);
      self._resize(); // sizes the renderer + fits the mesh into the canvas frame
      self._reveal();
      self.enter();
      // re-fit once layout settles (header logo height, fonts, sticky measure)
      requestAnimationFrame(function () { self._resize(); });
      setTimeout(function () { self._resize(); }, 350);
    }).catch(function () { self._showFallback(); });
  };

  Glass.prototype.enter = function () {
    if (!this.mesh) return;
    this.renderOnce();
    if (this.motion === 'always') { this.start(); }
    else if (this.motion === 'intro') { this.stopAt = performance.now() + 1700; this.start(); }
    // hover: stay frozen; the pointerenter handler starts it
  };
  Glass.prototype._reveal = function () {
    this.canvas.style.opacity = '1';
    if (this.fallback) this.fallback.style.opacity = '0';
  };
  Glass.prototype._showFallback = function () {
    this.canvas.style.opacity = '0';
    if (this.fallback) this.fallback.style.opacity = '1';
  };
  Glass.prototype._resize = function () {
    // Render at the CANVAS size — CSS can grow it beyond the block (overflow mode)
    // so the motif can be pushed past the block / section edge.
    var w = this.canvas.clientWidth || this.el.clientWidth, h = this.canvas.clientHeight || this.el.clientHeight;
    if (!w || !h) return;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h; this.camera.updateProjectionMatrix();
    this._fit();
  };
  // Scale the mesh to fill `fill` of the BLOCK (K corrects for an oversized overflow
  // canvas so the motif keeps its size), using the rotation-safe extents and the
  // FRONT-FACE perspective magnification so nothing clips. Then nudge by the offset;
  // room is left below for the contact shadow.
  Glass.prototype._fit = function () {
    if (!this.mesh || !this._size) return;
    var camZ = this.camera.position.z, t = Math.tan(this.camera.fov * Math.PI / 360);
    var asp = this.camera.aspect;
    var ex = Math.max(this._size.x, this._size.z, 0.001); // Y-rotation swings depth into width
    var ey = Math.max(this._size.y, 0.001);
    var K = (this.canvas.clientWidth || 1) / Math.max(this.el.clientWidth, 1);
    var ef = this.fill / Math.max(K, 0.001);
    if (this.shadow) ef *= 0.82; // leave room below for the contact shadow
    var vh = 2 * camZ * t, vw = vh * asp;
    var s = Math.min(vw * ef / ex, vh * ef / ey);
    var halfDepth = (this._size.z * s) / 2;          // front face is closer → magnified
    var dz = Math.max(camZ - halfDepth, 0.5);
    var vhf = 2 * dz * t, vwf = vhf * asp;
    s = Math.min(vwf * ef / ex, vhf * ef / ey);
    this.mesh.scale.setScalar(s);
    var lift = this.shadow ? vh * 0.09 : 0;
    // Clamp the offset so the motif never crosses the CANVAS edge (= never gets a
    // hard clip). The canvas is sized (CSS) well beyond the block, so this still
    // lets the motif sit far past the block / section edge — it just can't be cut.
    var mhw = (ex * s) / 2, mhh = (ey * s) / 2;
    var maxX = Math.max(vw / 2 - mhw, 0), maxY = Math.max(vh / 2 - mhh, 0);
    // Offset is element-relative (ox=100% → one full element-width of travel), so
    // the whole ±100% range stays inside the over-sized canvas → never clipped.
    var px = Math.max(-maxX, Math.min(maxX, this.ox * vw / K));
    var py = Math.max(-maxY, Math.min(maxY, this.oy * vh / K + lift));
    this.mesh.position.set(px, py, 0);
    if (this.shadow) {
      var sw = Math.max(this._size.x, 0.001) * s, sh = ey * s;
      this.shadow.position.set(px, py - sh / 2 - sh * 0.06, -0.25);
      this.shadow.scale.set(Math.max(sw * 1.15, 0.2), Math.max(sh * 0.5, 0.1), 1);
    }
  };
  Glass.prototype._initShadow = function (strength) {
    var THREE = this.THREE;
    var c = document.createElement('canvas'); c.width = c.height = 128;
    var x = c.getContext('2d');
    var g = x.createRadialGradient(64, 64, 2, 64, 64, 62);
    g.addColorStop(0, 'rgba(0,0,0,0.65)'); g.addColorStop(0.55, 'rgba(0,0,0,0.28)'); g.addColorStop(1, 'rgba(0,0,0,0)');
    x.fillStyle = g; x.fillRect(0, 0, 128, 128);
    this.shadowTex = new THREE.CanvasTexture(c);
    var mat = new THREE.MeshBasicMaterial({ map: this.shadowTex, transparent: true, opacity: strength, depthWrite: false });
    this.shadow = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), mat);
    this.scene.add(this.shadow);
  };
  Glass.prototype.renderOnce = function () {
    if (this.mesh) this.renderer.render(this.scene, this.camera);
  };
  Glass.prototype.start = function () {
    if (this.running || !this.inView) return; this.running = true; this.last = 0;
    this.raf = requestAnimationFrame(this._loop.bind(this));
  };
  Glass.prototype.stop = function () {
    this.running = false; if (this.raf) cancelAnimationFrame(this.raf); this.raf = 0;
  };
  Glass.prototype._loop = function (now) {
    if (!this.running) return;
    if (this.stopAt && now >= this.stopAt) { this.stopAt = 0; this.renderOnce(); this.stop(); return; }
    this.raf = requestAnimationFrame(this._loop.bind(this));
    if (now - this.last < 16.5) return;
    var dt = this.last ? Math.min((now - this.last) / 1000, 0.05) : 0.016; this.last = now;
    if (this.mesh) {
      this.mesh.rotation.y += this.spin * dt;
      if (this.mouse) {
        var aim = this.pointer ? this.mx * 0.5 : 0, aimY = this.pointer ? this.my * 0.4 : 0;
        this.ty += (aim - this.ty) * 0.08; this.tx += (aimY - this.tx) * 0.08;
        this.mesh.rotation.z = -this.ty * 0.15; this.mesh.rotation.x = this.tx;
        this.mesh.rotation.y += this.ty * 0.02;
      }
    }
    this.renderer.render(this.scene, this.camera);
  };
  Glass.prototype.onView = function (v) {
    this.inView = v;
    if (!v) { this.stop(); return; }
    if (this.motion === 'always') this.start(); else this.renderOnce();
  };
  Glass.prototype.dispose = function () {
    this.stop();
    window.removeEventListener('resize', this._onResize);
    if (this._onMove) this.el.removeEventListener('pointermove', this._onMove);
    if (this._onEnter) { this.el.removeEventListener('pointerenter', this._onEnter); this.el.removeEventListener('pointerleave', this._onLeaveEl); }
    this.canvas.removeEventListener('webglcontextlost', this._onLost);
    if (this.mesh) this.mesh.geometry.dispose();
    if (this.shadow) { this.shadow.geometry.dispose(); this.shadow.material.dispose(); }
    if (this.shadowTex) this.shadowTex.dispose();
    if (this.mat) this.mat.dispose();
    if (this.scene && this.scene.environment) this.scene.environment.dispose();
    try { this.renderer.dispose(); var ext = this.renderer.getContext().getExtension('WEBGL_lose_context'); if (ext) ext.loseContext(); } catch (e) {}
  };

  var registry = [];
  function activate(el) {
    if (el.__neGlInit) return; el.__neGlInit = true;
    if (reduce || !hasWebGL()) return; // keep the flat fallback
    var go = function () {
      loadBundle(el.getAttribute('data-bundle')).then(function (NE) {
        var g = new Glass(el, NE); el.__neGlass = g; registry.push(g);
      }).catch(function () { /* fallback img stays */ });
    };
    if (el.getAttribute('data-defer') === 'idle' && window.requestIdleCallback) {
      requestIdleCallback(go, { timeout: 2500 });
    } else { go(); }
  }

  function rescan() {
    var list = document.querySelectorAll('[data-ne-glogo]');
    for (var i = 0; i < list.length; i++) {
      var el = list[i]; if (el.__neGlObserved) continue; el.__neGlObserved = true;
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          var g = en.target.__neGlass;
          if (en.isIntersecting) { activate(en.target); if (g) g.onView(true); }
          else if (g) g.onView(false);
        });
      }, { rootMargin: '300px' });
      io.observe(el); el.__neGlIO = io;
    }
  }
  function disposeIn(root) {
    registry = registry.filter(function (g) {
      if (root && root.contains && root.contains(g.el)) {
        if (g.el.__neGlIO) g.el.__neGlIO.disconnect();
        g.dispose(); return false;
      }
      return true;
    });
  }

  window.__neGlassLogo = { rescan: rescan, dispose: disposeIn };
  if (document.readyState !== 'loading') rescan();
  else document.addEventListener('DOMContentLoaded', rescan);
  document.addEventListener('shopify:section:load', function (e) { if (e.target) disposeIn(e.target); rescan(); });
  document.addEventListener('shopify:section:unload', function (e) { if (e.target) disposeIn(e.target); });
})();
